import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-medium transition-all duration-300 ease-in-out hover:duration-50 active:duration-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--text)] text-[var(--text-inverse)] hover:opacity-90 active:scale-[0.98]",
        destructive:
          "bg-[var(--error)] text-white hover:opacity-90 active:scale-[0.98]",
        outline:
          "border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-raised)] active:scale-[0.98]",
        secondary:
          "bg-[var(--surface-raised)] text-[var(--text)] hover:bg-[var(--border)] active:scale-[0.98]",
        ghost:
          "hover:bg-[var(--surface-raised)] active:scale-[0.98]",
        link:
          "text-[var(--accent-text)] underline-offset-4 hover:underline",
        accent:
          "bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
